// Bottom sheets and pickers for the itinerary editor: edit-item details
// (rename, duration, time, location), booking mini-sheets, the add-activity
// sheet, the scan-booking sheet (screenshot → /api/tp/scan-screenshot), and
// the shared TimePicker. All are controlled components — state lives in
// ItineraryScreen.
import React from 'react';
import { PALETTE } from '../../theme';
import { Icon, ImageModal, Chip } from '../../components/UI';
import { CAT_COLORS } from '../../data';
import { useAuth } from '../../contexts/AuthContext';
import { toTimeStr, parseTimeStr, legIndexForDay } from './logic';
import { compressImage } from '../../utils/image';
import { fmtDMY } from '../../utils/trips';
import { isSafeHttpUrl } from '../../utils/url';

export function LocationSearch({ value, onChange, placeholder = 'Area or neighbourhood' }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const debRef = React.useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debRef.current);
    if (val.length < 2) { setSuggestions([]); setOpen(false); return; }
    debRef.current = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=5&accept-language=en`;
        const resp = await fetch(url, { headers: { 'User-Agent': 'TravelPlannerApp/1.0' } });
        const data = await resp.json();
        const results = data.slice(0, 5).map(r => ({
          label: r.display_name.split(',').slice(0, 2).join(',').trim(),
          short: r.display_name.split(',')[0].trim(),
        }));
        setSuggestions(results);
        setOpen(results.length > 0);
      } catch { setSuggestions([]); }
    }, 400);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${PALETTE.rule}`, marginBottom: 14 }}>
        <Icon name="pin" size={15} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
        <input
          value={value}
          onChange={handleChange}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          style={{
            flex: 1, appearance: 'none', border: 0, outline: 'none', background: 'transparent',
            fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink,
            padding: '10px 0',
          }}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: PALETTE.card, borderRadius: 10,
          border: `1px solid ${PALETTE.rule}`,
          boxShadow: '0 6px 20px rgba(34,31,26,0.12)',
          marginTop: 4, overflow: 'hidden',
        }}>
          {suggestions.map((s, i) => (
            <button key={i} onMouseDown={() => { onChange(s.short); setOpen(false); }} style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '9px 12px', background: 'transparent', border: 0,
              borderBottom: i < suggestions.length - 1 ? `0.5px solid ${PALETTE.ruleSoft}` : 0,
              cursor: 'pointer', fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink,
            }}
            onMouseEnter={e => e.currentTarget.style.background = PALETTE.paperDim}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{s.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export const CUSTOM_CATS = ['Sights', 'Food', 'Experience', 'Day trip', 'Shopping', 'Nightlife', 'Hotel', 'Custom'];

// ── EditItemSheet ─────────────────────────────────────────────────────────────
export function EditItemSheet({ idea, isCustom = true, onSave, onClose, accent, startTime, onSaveTime, onDelete, tentative = false, onToggleConfirmed }) {
  const [title, setTitle] = React.useState(idea.title || '');
  const [dur, setDur] = React.useState(idea.dur || 1);
  const [area, setArea] = React.useState(idea.area || '');
  const [notes, setNotes] = React.useState(idea.meta || '');
  const [sourceUrl, setSourceUrl] = React.useState(idea.sourceUrl || '');
  const [requiresBooking, setRequiresBooking] = React.useState(!!idea.requiresBooking);
  const [localTime, setLocalTime] = React.useState(startTime ?? null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  // A trailing "?" reads as "I'm not sure about this yet" — auto-mark the
  // event tentative. Once the user explicitly clicks the toggle, that
  // manual choice wins and the "?" heuristic stops overriding it.
  // `tentative`/`onToggleConfirmed` flip state on the parent immediately
  // (unlike the rest of this form, which only saves on "Save changes"), so
  // the auto behavior mirrors that and calls onToggleConfirmed right away.
  const manualTentativeRef = React.useRef(false);
  const tentativeRef = React.useRef(tentative);
  tentativeRef.current = tentative;
  const onToggleConfirmedRef = React.useRef(onToggleConfirmed);
  onToggleConfirmedRef.current = onToggleConfirmed;
  React.useEffect(() => {
    if (!isCustom || manualTentativeRef.current || !onToggleConfirmedRef.current) return;
    const wantTentative = title.trim().endsWith('?');
    if (wantTentative !== tentativeRef.current) onToggleConfirmedRef.current();
  }, [title, isCustom]);
  const handleToggleConfirmed = () => { manualTentativeRef.current = true; onToggleConfirmed(); };
  // Area/notes/link collapse behind "More options" — but auto-open when the
  // idea already has any of them set, so editing never silently hides
  // existing data.
  const [expanded, setExpanded] = React.useState(!!(idea.area || idea.meta || idea.sourceUrl));

  const hasTime = startTime !== null && startTime !== undefined;
  const effectiveStart = localTime ?? startTime;
  const handleEndChange = (newEnd) => {
    setDur(Math.max(0.25, newEnd - effectiveStart));
  };

  const handleSave = () => {
    if (onSaveTime && localTime !== null) onSaveTime(localTime);
    if (isCustom) {
      onSave({ title: title.trim() || idea.title, dur, cat: idea.cat || 'Custom', area, meta: notes, sourceUrl: sourceUrl.trim(), requiresBooking });
    } else {
      onSave({ meta: notes, sourceUrl: sourceUrl.trim() });
    }
    onClose();
  };

  const labelStyle = { fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 6 };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 110,
      background: 'rgba(34,31,26,0.4)', display: 'flex', alignItems: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 680, margin: '0 auto',
        maxHeight: '80vh', background: PALETTE.paper,
        borderTopLeftRadius: 24, borderTopRightRadius: 24,
        padding: '12px 0 28px', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 12px' }} />
        <div style={{ padding: '0 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.4 }}>{isCustom ? 'Edit activity' : 'Activity details'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {isCustom && (
              <button
                title={requiresBooking ? 'Remind me to book — click to clear' : 'Remind me to book'}
                onClick={() => setRequiresBooking(r => !r)}
                style={{ appearance: 'none', border: 0, background: 'transparent', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', color: requiresBooking ? '#D97706' : PALETTE.inkMute }}
              >
                <Icon name="ticket" size={16} />
              </button>
            )}
            {onToggleConfirmed && (
              <button
                title={tentative ? 'Tentative / Confirmed — Tentative (click to confirm)' : 'Tentative / Confirmed — Confirmed (click to mark tentative)'}
                onClick={handleToggleConfirmed}
                style={{ appearance: 'none', border: 0, background: 'transparent', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', color: tentative ? PALETTE.inkMute : accent }}
              >
                <Icon name={tentative ? 'circleDashed' : 'circleCheck'} size={16} />
              </button>
            )}
            <button onClick={onClose} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '4px 24px 8px' }}>
          {isCustom ? (
            <>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="What are you doing?"
                style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1.5px solid ${PALETTE.rule}`, borderRadius: 12, padding: '12px 14px', background: PALETTE.card, fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink, outline: 'none', marginBottom: 10 }}
              />

              {/* Start / end / duration all on one row (Outlook-style):
                  editing start reflows end (duration fixed); editing end or
                  duration recomputes the other, previewing the resulting end
                  time right in the dropdown. Duration-only (no time context)
                  for items not currently on a day, e.g. edited from the map. */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                {hasTime && (
                  <>
                    <TimePicker value={effectiveStart} onChange={setLocalTime} />
                    <span style={{ color: PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 11 }}>–</span>
                    <TimePicker value={effectiveStart + dur} onChange={handleEndChange} />
                  </>
                )}
                <select
                  value={dur}
                  onChange={e => setDur(Number(e.target.value))}
                  style={{ flex: 1, minWidth: 130, boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10, padding: '9px 12px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none' }}
                >
                  {durOptionsFor(dur).map(({ val, label }) => (
                    <option key={val} value={val}>{hasTime ? `${label} · ends ${toTimeStr(effectiveStart + val)}` : label}</option>
                  ))}
                </select>
              </div>

              {/* Area / notes / link — a plain compact toggle rather than its
                  own bordered banner, so it doesn't eat vertical space. */}
              <button onClick={() => setExpanded(e => !e)} style={{ appearance: 'none', cursor: 'pointer', border: 0, background: 'transparent', padding: '2px 0', marginBottom: expanded ? 8 : 14, display: 'flex', alignItems: 'center', gap: 4, color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, fontWeight: 500 }}>
                <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={10} />
                Area, notes &amp; link
              </button>

              {expanded && (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <LocationSearch value={area} onChange={setArea} placeholder="Area or neighbourhood (optional)" />
                  </div>

                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Any notes or remarks… (optional)"
                    rows={2}
                    style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10, padding: '10px 14px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none', resize: 'none', marginBottom: 10 }}
                  />

                  <input
                    value={sourceUrl}
                    onChange={e => setSourceUrl(e.target.value)}
                    placeholder="Link / URL (optional)"
                    style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10, padding: '9px 12px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', marginBottom: 16 }}
                  />
                </>
              )}
            </>
          ) : (
            <>
              {/* Read-only info for preset ideas */}
              <div style={{ background: PALETTE.paperDim, borderRadius: 12, padding: '12px 16px', marginBottom: 14, border: `0.5px solid ${PALETTE.rule}` }}>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 8 }}>Pre-set activity (read-only)</div>
                <div style={{ fontFamily: 'Fraunces', fontSize: 20, letterSpacing: -0.3, color: PALETTE.ink, marginBottom: 6 }}>{idea.title}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkSoft, background: PALETTE.card, borderRadius: 6, padding: '3px 8px', border: `0.5px solid ${PALETTE.rule}` }}>{idea.cat}</span>
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkSoft, background: PALETTE.card, borderRadius: 6, padding: '3px 8px', border: `0.5px solid ${PALETTE.rule}` }}>{durLabel(idea.dur)}</span>
                  {idea.area && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkSoft, background: PALETTE.card, borderRadius: 6, padding: '3px 8px', border: `0.5px solid ${PALETTE.rule}` }}>{idea.area}</span>}
                </div>
                {isSafeHttpUrl(idea.sourceUrl) && (
                  <a href={idea.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8 }}><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M7 1h2v2M9 1L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> View source</a>
                )}
              </div>

              {hasTime && (
                <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div>
                    <div style={labelStyle}>Start time</div>
                    <TimePicker value={effectiveStart} onChange={setLocalTime} />
                  </div>
                  <span style={{ color: PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 11, marginTop: 14 }}>
                    ends {toTimeStr(effectiveStart + dur)}
                  </span>
                </div>
              )}

              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add your own notes for this activity… (optional)"
                rows={2}
                style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10, padding: '10px 14px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none', resize: 'none', marginBottom: 16 }}
              />
            </>
          )}

          <button onClick={handleSave} style={{
            appearance: 'none', border: 0, cursor: 'pointer',
            width: '100%', padding: '14px',
            background: PALETTE.ink, color: PALETTE.paper,
            borderRadius: 12, fontFamily: 'Inter', fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            marginBottom: onDelete ? 10 : 0,
          }}>
            Save changes
          </button>

          {onDelete && (
            !confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, display: 'block', margin: '0 auto' }}>
                Remove from trip
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Remove for good?</span>
                <button onClick={() => { onDelete(); onClose(); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, fontWeight: 600 }}>Yes, remove</button>
                <button onClick={() => setConfirmDelete(false)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Cancel</button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ── BookMiniSheet ─────────────────────────────────────────────────────────────
export function BookMiniSheet({ idea, bookingInfo, isBooked, onToggleBooked, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 120,
      background: 'rgba(34,31,26,0.4)', display: 'flex', alignItems: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 680, margin: '0 auto',
        background: PALETTE.paper,
        borderTopLeftRadius: 24, borderTopRightRadius: 24,
        padding: '12px 0 36px', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 16px' }} />
        <div style={{ padding: '0 24px' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 6 }}>Book ticket</div>
          <div style={{ fontFamily: 'Fraunces', fontSize: 24, letterSpacing: -0.5, color: PALETTE.ink, marginBottom: 4 }}>{idea.title}</div>
          {bookingInfo?.estimatedCost && (
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginBottom: 16 }}>
              ~{bookingInfo.currency}{bookingInfo.estimatedCost} per person
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {(bookingInfo?.links || []).map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 10,
                background: i === 0 ? PALETTE.ink : PALETTE.paperDim,
                color: i === 0 ? PALETTE.paper : PALETTE.ink,
                fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
                border: i === 0 ? 'none' : `0.5px solid ${PALETTE.rule}`,
                textDecoration: 'none',
              }}>
                {link.label} <Icon name="arrow" size={13} />
              </a>
            ))}
          </div>
          <button
            onClick={() => { onToggleBooked?.(idea.id, !isBooked); onClose(); }}
            style={{
              appearance: 'none', border: 0, cursor: 'pointer',
              width: '100%', padding: '14px',
              background: isBooked ? PALETTE.paperDim : PALETTE.accent,
              color: isBooked ? PALETTE.inkSoft : '#fff',
              borderRadius: 12, fontFamily: 'Inter', fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.2s',
            }}
          >
            {isBooked
              ? <><Icon name="x" size={16} /> Mark as not booked</>
              : <><Icon name="check" size={16} stroke={2.5} /> Mark as booked</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export function BookedDetailSheet({ idea, details = {}, onClose, onToggleBooked, onOpenBookings }) {
  const fields = [
    { key: 'confirmationNo', label: 'Ref / Conf. No.' },
    { key: 'bookingUrl', label: 'Booking URL' },
    { key: 'notes', label: 'Notes' },
  ];
  const hasAny = fields.some(f => details[f.key]?.trim?.()) || !!details.screenshot;
  const [lightboxSrc, setLightboxSrc] = React.useState(null);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(34,31,26,0.4)', display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 680, margin: '0 auto', background: PALETTE.paper, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 0 36px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 16px' }} />
        <div style={{ padding: '0 24px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.green, marginBottom: 4 }}>✓ Booked</div>
              <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.4, color: PALETTE.ink, lineHeight: 1.2 }}>{idea.title}</div>
            </div>
            <button onClick={() => { onToggleBooked?.(idea.id, false); onClose(); }} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '6px 12px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, cursor: 'pointer', background: 'transparent', marginTop: 4, flexShrink: 0 }}>Unmark</button>
          </div>

          {hasAny ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {fields.filter(f => details[f.key]?.trim?.()).map(f => (
                <div key={f.key} style={{ display: 'flex', gap: 10 }}>
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', minWidth: 80, paddingTop: 2 }}>{f.label}</span>
                  {f.key === 'bookingUrl'
                    ? <a href={details[f.key]} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.accent, textDecoration: 'none', wordBreak: 'break-all' }}>{details[f.key].replace(/^https?:\/\//, '').slice(0, 50)}</a>
                    : <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink }}>{details[f.key]}</span>
                  }
                </div>
              ))}
              {details.screenshot && (
                <img src={details.screenshot} alt="screenshot" onClick={() => setLightboxSrc(details.screenshot)} style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, border: `0.5px solid ${PALETTE.rule}`, cursor: 'pointer', marginTop: 4 }} />
              )}
              {lightboxSrc && <ImageModal src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
            </div>
          ) : (
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, fontStyle: 'italic', marginBottom: 20 }}>No booking details saved yet.</div>
          )}

          <button onClick={onOpenBookings} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', borderRadius: 10, padding: '11px 16px', fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}>
            {hasAny ? 'Edit details in Bookings tab →' : 'Add details in Bookings tab →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Field definitions for the in-place editor, one set per leg kind — same
// shape as BookingScreen.js's legEditFields, kept independent since this
// sheet is a lighter-weight popup (opened straight from clicking a
// flight/activity band on the Calendar), not the full Bookings tab.
const LEG_DETAIL_FIELDS = {
  activity: [
    { key: 'title', label: 'What is it?', ph: 'e.g. Sagrada Familia tour' },
    { key: 'date', label: 'Date', ph: '', type: 'date' },
    { key: 'time', label: 'Time', ph: '', type: 'time' },
    { key: 'confirmation', label: 'Confirmation No.', ph: 'e.g. AB-20260001' },
    { key: 'notes', label: 'Notes', ph: '' },
  ],
  flight: [
    { key: 'flightNo', label: 'Flight No.', ph: 'e.g. SQ469' },
    { key: 'airline', label: 'Airline', ph: 'e.g. Singapore Airlines' },
    { key: 'date', label: 'Date', ph: '', type: 'date' },
    { key: 'depTime', label: 'Departs', ph: '', type: 'time' },
    { key: 'origin', label: 'From (airport)', ph: 'e.g. SIN' },
    { key: 'arrTime', label: 'Arrives', ph: '', type: 'time' },
    { key: 'dest', label: 'To (airport)', ph: 'e.g. NRT' },
    { key: 'confirmation', label: 'Booking Ref / PNR', ph: 'e.g. ABC123' },
  ],
};

// Popup for a single booked flight/activity leg — opened by clicking its
// band directly on the Calendar grid. Shows the saved details (screenshot
// included, at the bottom) and lets you edit or remove it in place, without
// leaving the itinerary for the Bookings tab.
export function LegDetailSheet({ leg, kind, onSave, onRemove, onClose }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(leg);
  const [lightboxSrc, setLightboxSrc] = React.useState(null);
  const [uploadingSS, setUploadingSS] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const fileRef = React.useRef(null);

  React.useEffect(() => { setDraft(leg); setEditing(false); }, [leg]);

  const isActivity = kind === 'activity';
  const icon = isActivity ? '🎟️' : '✈️';
  const primaryLabel = isActivity ? (leg.title || 'Activity') : ([leg.origin, leg.dest].filter(Boolean).join(' → ') || 'Flight');
  const fields = LEG_DETAIL_FIELDS[kind] || LEG_DETAIL_FIELDS.activity;

  const saveEdit = () => { onSave(draft); setEditing(false); };
  const cancelEdit = () => { setDraft(leg); setEditing(false); };

  const handleScreenshotFile = async (file) => {
    if (!file) return;
    setUploadingSS(true);
    try {
      const b64 = await compressImage(file);
      const nd = { ...draft, screenshot: b64 };
      setDraft(nd);
      onSave(nd);
    } finally { setUploadingSS(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const rows = isActivity
    ? [
        { label: 'When', value: [leg.date && fmtDMY(leg.date), leg.time].filter(Boolean).join(' · ') },
        { label: 'Confirmation', value: leg.confirmation },
        { label: 'Notes', value: leg.notes },
      ]
    : [
        { label: 'Flight', value: [leg.flightNo, leg.airline].filter(Boolean).join(' · ') },
        { label: 'Date', value: leg.date && fmtDMY(leg.date) },
        { label: 'Departs', value: [leg.depTime, leg.origin].filter(Boolean).join(' · ') },
        { label: 'Arrives', value: [leg.arrTime, leg.dest].filter(Boolean).join(' · ') },
        { label: 'Confirmation', value: leg.confirmation },
      ];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(34,31,26,0.4)', display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 680, margin: '0 auto', background: PALETTE.paper, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 0 28px', maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 12px' }} />
        <div style={{ padding: '0 24px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
              <div style={{ fontFamily: 'Fraunces', fontSize: 20, letterSpacing: -0.3, color: PALETTE.ink, lineHeight: 1.2 }}>{primaryLabel}</div>
            </div>
            <button onClick={onClose} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>Close</button>
          </div>

          {!editing ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {rows.filter(r => r.value).map(r => (
                  <div key={r.label} style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', minWidth: 90, paddingTop: 2, flexShrink: 0 }}>{r.label}</span>
                    <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink }}>{r.value}</span>
                  </div>
                ))}
                {leg.screenshot && (
                  <img src={leg.screenshot} alt="screenshot" onClick={() => setLightboxSrc(leg.screenshot)} style={{ maxWidth: 90, maxHeight: 64, borderRadius: 6, border: `0.5px solid ${PALETTE.rule}`, objectFit: 'cover', cursor: 'pointer', marginTop: 4, display: 'block' }} />
                )}
                {lightboxSrc && <ImageModal src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
              </div>

              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleScreenshotFile(e.target.files?.[0])} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button onClick={() => setEditing(true)} style={{ appearance: 'none', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', borderRadius: 10, background: PALETTE.ink, color: PALETTE.paper, border: 0, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
                  <Icon name="pencil" size={13} /> Edit
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={uploadingSS} style={{ appearance: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', borderRadius: 10, background: 'transparent', border: `1px solid ${PALETTE.rule}`, color: PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 13, fontWeight: 500 }}>
                  <Icon name="camera" size={13} /> {uploadingSS ? 'Uploading…' : leg.screenshot ? 'Replace' : 'Add screenshot'}
                </button>
              </div>
              {!confirmRemove ? (
                <button onClick={() => setConfirmRemove(true)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>
                  Remove this {kind === 'activity' ? 'booking' : 'flight'}
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Remove for good?</span>
                  <button onClick={() => { onRemove(); onClose(); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, fontWeight: 600 }}>Yes, remove</button>
                  <button onClick={() => setConfirmRemove(false)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Cancel</button>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                {fields.map(f => (
                  <div key={f.key}>
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
                    <input
                      type={f.type || 'text'}
                      value={draft[f.key] || ''}
                      onChange={e => setDraft(d => ({ ...d, [f.key]: (f.key === 'origin' || f.key === 'dest') ? e.target.value.toUpperCase() : e.target.value }))}
                      placeholder={f.ph}
                      style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.card, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveEdit} style={{ appearance: 'none', border: 0, cursor: 'pointer', flex: 1, padding: '10px 14px', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 10, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>Save</button>
                <button onClick={cancelEdit} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', cursor: 'pointer', padding: '10px 14px', borderRadius: 10, color: PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 13 }}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export const DUR_PRESETS = [
  { val: 0.5, label: '30m' }, { val: 1, label: '1h' }, { val: 1.5, label: '1.5h' },
  { val: 2, label: '2h' }, { val: 3, label: '3h' }, { val: 4, label: '4h' },
  { val: 6, label: '6h' }, { val: 8, label: 'Full day' },
];

function durLabel(h) {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (Number.isInteger(h)) return `${h}h`;
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h ${mins}m`;
}

// A Duration <select> bound to a value that isn't one of DUR_PRESETS (e.g.
// dragged out to 2h15m on the Calendar, which has no matching preset) will
// silently render its FIRST option as selected — the browser's default
// fallback for an unmatched <option>. That showed "30m" for a genuine 2h15m
// selection even though the underlying value was correct. Injecting the
// current value as its own option, in sorted position, keeps what's
// displayed always truthful.
function durOptionsFor(current) {
  if (DUR_PRESETS.some(p => p.val === current)) return DUR_PRESETS;
  return [...DUR_PRESETS, { val: current, label: durLabel(current) }].sort((a, b) => a.val - b.val);
}

// Every half-hour of the day, 12:00am–11:30pm — the dropdown list an Outlook
// time field offers alongside its free-text input.
const TIME_SLOT_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 0.5);

export function TimePicker({ value, onChange }) {
  const [text, setText] = React.useState(() => toTimeStr(value));
  const [focused, setFocused] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const listRef = React.useRef(null);
  React.useEffect(() => { if (!focused) setText(toTimeStr(value)); }, [value]); // eslint-disable-line

  const commit = (raw) => {
    const parsed = parseTimeStr(raw);
    if (parsed !== null) { onChange(parsed); setText(toTimeStr(parsed)); }
    else setText(toTimeStr(value));
  };

  const selectOption = (v) => {
    onChange(v); setText(toTimeStr(v)); setOpen(false);
  };

  // Scroll the option nearest the current value into view when the list opens
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    let nearestIdx = 0;
    TIME_SLOT_OPTIONS.forEach((t, i) => {
      if (Math.abs(t - value) < Math.abs(TIME_SLOT_OPTIONS[nearestIdx] - value)) nearestIdx = i;
    });
    listRef.current.children[nearestIdx]?.scrollIntoView({ block: 'center' });
  }, [open]); // eslint-disable-line

  React.useEffect(() => {
    if (!open) return;
    const dismiss = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    window.addEventListener('pointerdown', dismiss);
    return () => window.removeEventListener('pointerdown', dismiss);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        value={focused ? text : toTimeStr(value)}
        onFocus={(e) => { setFocused(true); setText(toTimeStr(value)); setOpen(true); e.currentTarget.select(); }}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => { setFocused(false); commit(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setOpen(false); }}
        placeholder="9:00 am"
        style={{
          fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: PALETTE.ink,
          background: PALETTE.paper, border: `1px solid ${focused ? PALETTE.accent : PALETTE.rule}`,
          borderRadius: 8, padding: '6px 12px', width: 104,
          outline: 'none', boxSizing: 'border-box', textAlign: 'center', transition: 'border-color 0.15s',
        }}
      />
      {open && (
        <div ref={listRef} style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 30,
          width: 112, maxHeight: 190, overflowY: 'auto', scrollbarWidth: 'thin',
          background: PALETTE.paper, border: `1px solid ${PALETTE.rule}`, borderRadius: 8,
          boxShadow: '0 8px 20px rgba(0,0,0,0.16)',
        }}>
          {TIME_SLOT_OPTIONS.map(t => {
            const isCurrent = Math.abs(t - value) < 0.01;
            return (
              <div
                key={t}
                onMouseDown={(e) => { e.preventDefault(); selectOption(t); }}
                style={{
                  padding: '6px 12px', fontFamily: 'Inter', fontSize: 13, cursor: 'pointer',
                  background: isCurrent ? `${PALETTE.accent}18` : 'transparent',
                  color: isCurrent ? PALETTE.accent : PALETTE.ink, fontWeight: isCurrent ? 700 : 500,
                }}
              >
                {toTimeStr(t)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export function AddSheet({ dayIdx, selectedIds, allIdeas, onAdd, onAddCustom, onSaveBookingDetails, onClose, accent, initialStartHour = null, initialDur = null, legs = null }) {
  // 'custom' (default — type your own activity) | 'browse' (preset idea list,
  // reached via "Give me some ideas")
  const [panel, setPanel] = React.useState('custom');
  const [query, setQuery] = React.useState('');
  // Multi-city trips: default the browse list to whichever city this day
  // actually belongs to, so tapping into the Brussels block doesn't surface
  // Barcelona ideas — "All cities" is still one tap away.
  const isMultiCity = legs && legs.length > 1;
  const currentCity = isMultiCity ? (legs[legIndexForDay(legs, dayIdx)]?.destination || '').split(',')[0].trim() : null;
  const allCities = isMultiCity ? ['All', ...new Set(legs.map(l => (l.destination || '').split(',')[0].trim()))] : null;
  const [cityFilter, setCityFilter] = React.useState(currentCity || 'All');
  const [title, setTitle] = React.useState('');
  // Dragging out a time range on the calendar already fixes the duration and
  // start — reflect both back here instead of silently defaulting and
  // relying on invisible overrides the user never sees.
  const [customStart, setCustomStart] = React.useState(initialStartHour ?? 9);
  const [customDur, setCustomDur] = React.useState(initialDur ?? 1);
  const [customCat] = React.useState('Custom');
  const [customArea, setCustomArea] = React.useState('');
  const [customNotes, setCustomNotes] = React.useState('');
  const [needsBooking, setNeedsBooking] = React.useState(false);
  const [tentative, setTentative] = React.useState(false);
  // A trailing "?" reads as "I'm not sure about this yet" — auto-mark the
  // event tentative. Once the user explicitly clicks the toggle, that
  // manual choice wins and the "?" heuristic stops overriding it.
  const manualTentativeRef = React.useRef(false);
  React.useEffect(() => {
    if (manualTentativeRef.current) return;
    setTentative(title.trim().endsWith('?'));
  }, [title]);
  const toggleTentative = () => { manualTentativeRef.current = true; setTentative(t => !t); };
  const [screenshot, setScreenshot] = React.useState(null);
  const [screenshotName, setScreenshotName] = React.useState('');
  const [uploadingSS, setUploadingSS] = React.useState(false);
  const fileRef = React.useRef(null);
  // Area/notes/booking confirmation collapse behind "More options" — this is
  // a brand-new item, so there's nothing existing to preserve by defaulting
  // open (contrast with EditItemSheet, which auto-opens when the idea
  // already has one of these set).
  const [expanded, setExpanded] = React.useState(false);

  const hasTime = initialStartHour != null;
  const handleEndChange = (newEnd) => setCustomDur(Math.max(0.25, newEnd - customStart));

  const candidates = allIdeas.filter(i => !selectedIds.includes(i.id) && (!isMultiCity || cityFilter === 'All' || i.destCity === cityFilter));
  const q = query.trim();
  const filtered = q ? candidates.filter(i => i.title.toLowerCase().includes(q.toLowerCase())) : candidates;

  const handleScreenshotFile = async (file) => {
    if (!file) return;
    setUploadingSS(true);
    try {
      const b64 = await compressImage(file);
      setScreenshot(b64);
      setScreenshotName(file.name);
      setNeedsBooking(true);
    } finally { setUploadingSS(false); }
  };

  const handleAddCustom = () => {
    if (!title.trim()) return;
    const idea = {
      id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      title: title.trim(), cat: customCat, dur: customDur,
      area: customArea.trim(), meta: customNotes.trim(),
      requiresBooking: needsBooking,
      // Typed straight into a specific day, so the leg it belongs to is
      // unambiguous — no need to ask, unlike the standalone idea bar.
      ...(isMultiCity ? { legIndex: legIndexForDay(legs, dayIdx), destCity: currentCity } : {}),
    };
    onAddCustom(idea, { ...(hasTime ? { startTime: customStart } : {}), ...(tentative ? { tentative } : {}) });
    if (screenshot) onSaveBookingDetails?.(idea.id, { screenshot, screenshotName });
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(34,31,26,0.4)', display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 680, margin: '0 auto', maxHeight: '78vh', background: PALETTE.paper, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 0 28px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 12px' }} />
        <div style={{ padding: '0 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.4 }}>
            {panel === 'browse' ? (isMultiCity && cityFilter !== 'All' ? `Some ideas · ${cityFilter}` : 'Some ideas') : `Add to Day ${dayIdx + 1}`}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {panel !== 'custom' && (
              <button onClick={() => setPanel('custom')} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, cursor: 'pointer', padding: 0 }}>← Back</button>
            )}
            {panel === 'custom' && (
              <>
                <button
                  title={needsBooking ? 'Remind me to book — click to clear' : 'Remind me to book'}
                  onClick={() => setNeedsBooking(r => !r)}
                  style={{ appearance: 'none', border: 0, background: 'transparent', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', color: needsBooking ? '#D97706' : PALETTE.inkMute }}
                >
                  <Icon name="ticket" size={16} />
                </button>
                <button
                  title={tentative ? 'Tentative / Confirmed — Tentative (click to confirm)' : 'Tentative / Confirmed — Confirmed (click to mark tentative)'}
                  onClick={toggleTentative}
                  style={{ appearance: 'none', border: 0, background: 'transparent', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', color: tentative ? PALETTE.inkMute : accent }}
                >
                  <Icon name={tentative ? 'circleDashed' : 'circleCheck'} size={16} />
                </button>
              </>
            )}
            <button onClick={onClose} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>

        {panel === 'browse' ? (
          <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px' }}>
            <div style={{ padding: '0 0 10px' }}>
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search ideas…"
                style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1.5px solid ${PALETTE.rule}`, borderRadius: 12, padding: '12px 14px', background: PALETTE.card, fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink, outline: 'none' }}
              />
            </div>
            {isMultiCity && (
              <div style={{ padding: '0 0 10px', display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {allCities.map(c => <Chip key={c} active={cityFilter === c} onClick={() => setCityFilter(c)}>{c}</Chip>)}
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: '20px 0', color: PALETTE.inkMute, fontFamily: 'Fraunces', fontStyle: 'italic', fontSize: 15 }}>
                {q ? 'No matching ideas.' : "Everything's already in your trip."}
              </div>
            )}
            {filtered.map(it => (
              <button key={it.id} onClick={() => onAdd(it.id, tentative ? { tentative } : {})} style={{ appearance: 'none', border: 0, cursor: 'pointer', width: '100%', textAlign: 'left', padding: '12px 0', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, background: 'transparent' }}>
                <div style={{ width: 4, height: 28, borderRadius: 2, background: CAT_COLORS[it.cat] || PALETTE.inkMute, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Fraunces', fontSize: 16, color: PALETTE.ink }}>{it.title}</div>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5, marginTop: 2 }}>
                    {isMultiCity && cityFilter === 'All' && it.destCity && <span style={{ color: accent }}>{it.destCity} · </span>}
                    {it.cat}{it.area ? ` · ${it.area}` : ''} · {it.dur < 1 ? `${it.dur * 60}m` : `${it.dur}h`}
                  </div>
                </div>
                <div style={{ color: accent, flexShrink: 0 }}><Icon name="plus" size={20} stroke={2.5} /></div>
              </button>
            ))}
          </div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 24px 0' }}>
            {/* Custom activity — the default way to add something. The
                "browse existing ideas" entry point is a small icon next to
                the input rather than its own banner row. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                autoFocus
                value={title}
                onChange={e => setTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
                placeholder="What are you doing?"
                style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', appearance: 'none', border: `1.5px solid ${PALETTE.rule}`, borderRadius: 12, padding: '12px 14px', background: PALETTE.card, fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink, outline: 'none' }}
              />
              <button
                onClick={() => setPanel('browse')}
                title="Give me some ideas"
                aria-label="Give me some ideas"
                style={{ appearance: 'none', cursor: 'pointer', flexShrink: 0, width: 44, height: 44, borderRadius: 12, background: 'transparent', border: `1.5px solid ${PALETTE.rule}`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Icon name="sparkle" size={18} />
              </button>
            </div>

            {/* Start / end / duration on one row (Outlook-style) — same
                layout as EditItemSheet so adding and editing stay aligned. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
              {hasTime && (
                <>
                  <TimePicker value={customStart} onChange={setCustomStart} />
                  <span style={{ color: PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 11 }}>–</span>
                  <TimePicker value={customStart + customDur} onChange={handleEndChange} />
                </>
              )}
              <select
                value={customDur}
                onChange={e => setCustomDur(Number(e.target.value))}
                style={{ flex: 1, minWidth: 130, boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10, padding: '9px 12px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none' }}
              >
                {durOptionsFor(customDur).map(({ val, label }) => (
                  <option key={val} value={val}>{hasTime ? `${label} · ends ${toTimeStr(customStart + val)}` : label}</option>
                ))}
              </select>
            </div>

            {/* Area / notes / booking confirmation — a plain compact toggle
                rather than its own bordered banner, so it doesn't eat
                vertical space. */}
            <button onClick={() => setExpanded(e => !e)} style={{ appearance: 'none', cursor: 'pointer', border: 0, background: 'transparent', padding: '2px 0', marginBottom: expanded ? 8 : 14, display: 'flex', alignItems: 'center', gap: 4, color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, fontWeight: 500 }}>
              <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={10} />
              Area, notes &amp; booking confirmation
            </button>

            {expanded && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <LocationSearch value={customArea} onChange={setCustomArea} placeholder="Area or neighbourhood (optional)" />
                </div>

                <textarea
                  value={customNotes}
                  onChange={e => setCustomNotes(e.target.value)}
                  placeholder="Any notes or remarks… (optional)"
                  rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10, padding: '10px 14px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none', resize: 'none', marginBottom: 10 }}
                />

                {/* Screenshot — optional, feeds straight into the Bookings tab */}
                <div style={{ marginBottom: 16 }}>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleScreenshotFile(e.target.files?.[0])} />
                  {uploadingSS ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${accent}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>Uploading…</span>
                      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                  ) : !screenshot ? (
                    <div onClick={() => fileRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12 }}>
                      <Icon name="camera" size={13} /> Add a booking confirmation screenshot (optional)
                    </div>
                  ) : (
                    <div>
                      <img src={screenshot} alt="screenshot" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, border: `0.5px solid ${PALETTE.rule}`, display: 'block', marginBottom: 6 }} />
                      <button onClick={() => { setScreenshot(null); setScreenshotName(''); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="x" size={11} /> Remove screenshot
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            <button onClick={handleAddCustom} disabled={!title.trim()} style={{ appearance: 'none', border: 0, cursor: title.trim() ? 'pointer' : 'default', width: '100%', padding: '14px', background: title.trim() ? accent : PALETTE.rule, color: title.trim() ? '#fff' : PALETTE.inkMute, borderRadius: 12, fontFamily: 'Inter', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
              <Icon name="plus" size={16} /> Add to Day {dayIdx + 1}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ScanSheet ─────────────────────────────────────────────────────────────────

export function ScanSheet({ onClose, onApply, accent }) {
  const { user } = useAuth();
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const fileRef = React.useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f); setResult(null); setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  React.useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          handleFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  const handleScan = async () => {
    if (!file) return;
    setLoading(true); setError('');
    try {
      const reader = new FileReader();
      const base64 = await new Promise((res, rej) => {
        reader.onload = (ev) => res(ev.target.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const token = user ? await user.getIdToken() : '';
      const r = await fetch('/api/tp/scan-screenshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageData: base64, mimeType: file.type || 'image/jpeg' }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Scan failed');
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const hasDetails = result && (result.flightDetails || result.hotelDetails || result.activityDetails);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(34,31,26,0.4)', display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 680, margin: '0 auto', maxHeight: '80vh', background: PALETTE.paper, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 0 28px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 12px' }} />
        <div style={{ padding: '0 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.4, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="camera" size={20} stroke={1.5} /> Scan booking</div>
          <button onClick={onClose} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px' }}>
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginBottom: 14, lineHeight: 1.5 }}>
            Upload a flight confirmation, hotel booking, or activity receipt — Claude will extract the details.
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 14, padding: 24, textAlign: 'center', cursor: 'pointer', marginBottom: 16, background: PALETTE.card, transition: '0.15s' }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
            {preview ? (
              <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 10, objectFit: 'contain' }} />
            ) : (
              <>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute }}>Tap to upload, or paste (Ctrl+V)</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.rule, marginTop: 4 }}>PNG · JPG · WEBP</div>
              </>
            )}
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: PALETTE.redSoft, borderRadius: 10, color: PALETTE.red, fontFamily: 'Inter', fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          {result && (
            <div style={{ background: PALETTE.paperDim, borderRadius: 12, padding: '14px 16px', marginBottom: 16, border: `0.5px solid ${PALETTE.rule}` }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 8 }}>Extracted</div>
              <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginBottom: 10 }}>{result.summary}</div>
              {result.flightDetails && <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, marginBottom: 6 }}>✈️ {result.flightDetails}</div>}
              {result.hotelDetails && <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, marginBottom: 6 }}>🏨 {result.hotelDetails}</div>}
              {result.activityDetails && <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, marginBottom: 6 }}>🎯 {result.activityDetails}</div>}
              {result.bookingRef && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, marginTop: 6 }}>Ref: {result.bookingRef}</div>}
              {!hasDetails && <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.red }}>No travel details found — try a clearer screenshot.</div>}
            </div>
          )}

          {!result ? (
            <button onClick={handleScan} disabled={!file || loading} style={{ appearance: 'none', border: 0, cursor: file && !loading ? 'pointer' : 'default', width: '100%', padding: '14px', background: file && !loading ? PALETTE.ink : PALETTE.rule, color: file && !loading ? PALETTE.paper : PALETTE.inkMute, borderRadius: 12, fontFamily: 'Inter', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              {loading ? 'Scanning…' : 'Scan booking'}
            </button>
          ) : (
            <button onClick={() => onApply(result)} disabled={!hasDetails} style={{ appearance: 'none', border: 0, cursor: hasDetails ? 'pointer' : 'default', width: '100%', padding: '14px', background: hasDetails ? accent : PALETTE.rule, color: hasDetails ? '#fff' : PALETTE.inkMute, borderRadius: 12, fontFamily: 'Inter', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              Save to trip
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

